import {
  faChevronDown,
  faChevronUp,
  faEllipsis,
  faLock,
  faPencil,
  faShield,
  faShieldHalved,
  faSpinner,
  faUpload
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AutomatedInsuranceRunStatus from '@/components/AutomatedInsuranceRunStatus';
import CanSyncWithHealthieIcon from '@/components/CanSyncWithHealthieIcon';
import PayerResponse from '@/components/PayerResponse';
import PolicyDetailsList from '@/components/PolicyDetailsList';
import PolicyEditor from '@/components/PolicyEditor';
import PolicyFinancials from '@/components/PolicyFinancials';
import PolicyLinks from '@/components/PolicyLinks';
import TreatabilityBanner from '@/components/TreatabilityBanner';
import VerificationFreshness, {
  isVerificationStale,
  isVerifiedThroughLapsed
} from '@/components/VerificationFreshness';
import VerificationStatusPill from '@/components/VerificationStatusPill';
import AppContext from '@/contexts/appContext';
import useInsuranceDeepLinks from '@/hooks/useInsuranceDeepLinks';
import {
  useInsurancePolicyDetail,
  usePrefetchInsurancePolicyDetail
} from '@/hooks/useInsurancePolicyDetail';
import { isInsurancePolicyExpired } from '@/pages/Profile/utils/isInsurancePolicyExpired';
import { IN_PROGRESS_STATUS, InsuranceVerificationStatus } from '@/types/insuranceVerification';
import {
  Button,
  CopyContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Pill,
  Tooltip,
  VendorLogo
} from '@/ui';
import AuditLogLink from '@/ui/components/AuditLogLink';
import { daysBetween, formatNumericCalendarDate } from '@/utils/dates';
import { hasPermission, PEOPLE_RUN_VOB } from '@/utils/userPermission';

import VerificationNotes from './components/VerificationNotes';
import './styles.css';
import type { Props } from './types';

const ENDING_SOON_DAYS = 30;

const formatShortDate = (date: string) => formatNumericCalendarDate(date);

const daysUntil = (date: string) => {
  const target = new Date(`${date}T23:59:59`);
  return Number(daysBetween(new Date(), target));
};

const ExpanderButton = ({
  label,
  isOpen,
  onToggle,
  onWarm
}: {
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  onWarm?: () => void;
}) => (
  <button
    type="button"
    aria-expanded={isOpen}
    onClick={onToggle}
    onMouseEnter={onWarm}
    onFocus={onWarm}
    className="inline-flex items-center gap-1 self-start text-sm font-medium text-blue-700">
    {label}
    <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="h-3 w-3" />
  </button>
);

const CardImageThumbnail = ({
  label,
  url,
  isUpdatedAfterVerification,
  updatedAt,
  verifiedAt
}: {
  label: string;
  url: string;
  isUpdatedAfterVerification: boolean;
  updatedAt?: string;
  verifiedAt?: string;
}) => {
  const thumbnail = (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      aria-label={`View ${label.toLowerCase()} of card`}
      className="relative flex h-9 w-14 flex-col items-center justify-center rounded border border-uniform-gray-300 bg-uniform-gray-50 text-[11px] text-uniform-gray-600">
      {label}
      {isUpdatedAfterVerification && (
        <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-yellow-400 text-yellow-900">
          <FontAwesomeIcon icon={faUpload} className="h-2 w-2" />
        </span>
      )}
    </a>
  );

  if (!isUpdatedAfterVerification) return thumbnail;

  return (
    <Tooltip
      content={`Card images updated ${updatedAt ? new Date(updatedAt).toLocaleDateString('en-US') : ''}, after the last verification${verifiedAt ? ` (${new Date(verifiedAt).toLocaleDateString('en-US')})` : ''}.`}>
      {thumbnail}
    </Tooltip>
  );
};

const isCardUpdatedAfterVerification = (
  cardUpdatedAt: string | undefined,
  verifiedAt: string | undefined
) => !!cardUpdatedAt && !!verifiedAt && new Date(cardUpdatedAt) > new Date(verifiedAt);

const InsurancePolicyCard = ({
  policy,
  personId,
  automatedRun,
  onVerify,
  onRefreshRequested
}: Props) => {
  const navigate = useNavigate();
  const { addMessage, host, permissions } = useContext(AppContext);
  const { billingTimelineUrl } = useInsuranceDeepLinks(personId);
  const prefetchPolicyDetail = usePrefetchInsurancePolicyDetail();

  const [isEditing, setIsEditing] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isPayerResponseOpen, setIsPayerResponseOpen] = useState(false);

  const { data: fullPolicy, isLoading: isDetailLoading } = useInsurancePolicyDetail(policy.id, {
    enabled: isPayerResponseOpen
  });

  const canRunVob = hasPermission({ permissions, requiredPermission: PEOPLE_RUN_VOB });

  const verification = policy.last_verification;
  const epv = verification?.external_policy_verification;
  const isExpired = isInsurancePolicyExpired(policy.plan_end_date);
  const endsInDays = policy.plan_end_date && !isExpired ? daysUntil(policy.plan_end_date) : null;

  const isLocalRunActive = automatedRun?.state === 'running';
  const isRunning =
    isLocalRunActive ||
    automatedRun?.state === 'slow' ||
    policy.verification_status === IN_PROGRESS_STATUS;
  const needsAttention =
    !verification ||
    isVerificationStale(verification.verified_at) ||
    policy.verification_status === InsuranceVerificationStatus.FAILED ||
    policy.verification_status === InsuranceVerificationStatus.PENDING;

  if (policy.is_self_pay) {
    return (
      <article
        aria-label={`${policy.carrier ?? 'Self Pay'} policy`}
        className="rounded-lg border border-uniform-gray-300 bg-white shadow-sm">
        <div className="flex items-center gap-2.5 px-4 py-3">
          <h3 className="text-base font-semibold">Self Pay</h3>
          <Pill variant="outline" color="neutral" size="sm">
            Not applicable
          </Pill>
          <span className="text-sm text-uniform-gray-500">
            {`Plan dates ${policy.plan_start_date ? formatShortDate(policy.plan_start_date) : '—'} → ${policy.plan_end_date ? formatShortDate(policy.plan_end_date) : '—'}`}
          </span>
          <div className="grow" />
          {policy.id && (
            <AuditLogLink
              entityType="insurancepolicy"
              entityId={policy.id}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md"
            />
          )}
          <Button
            variant="outline"
            color="neutral"
            size="icon-md"
            aria-label="Edit plan dates"
            onClick={() => setIsEditing(true)}>
            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
          </Button>
        </div>
        {isEditing && (
          <div className="border-t border-uniform-gray-100 p-4">
            <PolicyEditor policy={policy} onClose={() => setIsEditing(false)} />
          </div>
        )}
      </article>
    );
  }

  const verificationColumn = (
    <div className="flex min-w-0 flex-col gap-3 p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-uniform-gray-500">
        Current verification
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <VerificationStatusPill status={policy.verification_status} />
        {verification && !isRunning && (
          <VerificationFreshness verifiedAt={verification.verified_at} />
        )}
        {automatedRun?.state === 'succeeded' && (
          <Pill color="secondary" variant="filled" size="sm">
            New
          </Pill>
        )}
      </div>

      {automatedRun && automatedRun.state !== 'succeeded' && (
        <AutomatedInsuranceRunStatus
          state={automatedRun.state}
          vendor={automatedRun.options?.vendor}
          serviceCode={automatedRun.options?.serviceCode}
          startedAt={automatedRun.startedAt}
          startedBy={automatedRun.startedBy}
          errorText={automatedRun.errorText}
          timelineUrl={policy.id ? billingTimelineUrl(policy.id) : null}
          onRetry={() => onVerify(policy, { tab: 'automated' })}
          onRunWithOptions={() => onVerify(policy, { tab: 'automated', showOptions: true })}
          onEnterManually={() => onVerify(policy, { tab: 'manual' })}
          onRefresh={onRefreshRequested}
        />
      )}

      {verification ? (
        <>
          <div className="flex flex-wrap items-center gap-1.5 text-sm text-uniform-gray-700">
            {verification.verified_at && (
              <span>{new Date(verification.verified_at).toLocaleDateString('en-US')}</span>
            )}
            {verification.verified_by?.first_name && (
              <span>
                by{' '}
                <strong>
                  {`${verification.verified_by.first_name} ${verification.verified_by.last_name ?? ''}`.trim()}
                </strong>
              </span>
            )}
            {epv?.source ? (
              <span className="inline-flex items-center gap-1">
                via <VendorLogo source={epv.source} size="sm" showName />
              </span>
            ) : (
              verification.verified_via && (
                <span>
                  via{' '}
                  {typeof verification.verified_via === 'string'
                    ? verification.verified_via
                    : verification.verified_via.name}
                </span>
              )
            )}
            {verification.verified_through && (
              <span>
                · through{' '}
                <strong
                  className={
                    isVerifiedThroughLapsed(verification.verified_through) ? 'text-yellow-800' : ''
                  }>
                  {formatShortDate(verification.verified_through)}
                </strong>
              </span>
            )}
          </div>

          <PolicyFinancials
            policy={policy}
            verification={verification}
            isMuted={isRunning}
            changedFrom={
              automatedRun?.state === 'succeeded' ? automatedRun.previousFinancials : null
            }
          />

          {epv && <PayerResponse verification={verification} view="summary" />}

          {verification.vob_notes && <VerificationNotes notes={verification.vob_notes} />}

          {epv && (
            <ExpanderButton
              label="Full payer response"
              isOpen={isPayerResponseOpen}
              onToggle={() => setIsPayerResponseOpen((open) => !open)}
              onWarm={() => prefetchPolicyDetail(policy.id)}
            />
          )}
        </>
      ) : (
        !isRunning && (
          <div className="flex items-start gap-3 rounded-md border border-dashed border-uniform-gray-300 p-3.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-uniform-gray-100 text-uniform-gray-600">
              <FontAwesomeIcon icon={faShield} className="h-4 w-4" />
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold">No verification on file</span>
              <span className="text-sm text-uniform-gray-500">
                {canRunVob
                  ? 'Use Verify to run Albert for a first check, or enter one manually.'
                  : 'Use Add verification to enter one manually.'}
              </span>
            </div>
          </div>
        )
      )}
    </div>
  );

  return (
    <article
      aria-label={`${policy.carrier ?? 'Insurance'} policy`}
      className="rounded-lg border border-uniform-gray-300 bg-white shadow-sm">
      <TreatabilityBanner explanation={policy.treatability_explanation} />
      <div className="flex flex-wrap items-center gap-2.5 border-b border-uniform-gray-100 py-2.5 pl-4 pr-3">
        <h3 className={`text-base font-semibold ${isExpired ? 'text-uniform-gray-500' : ''}`}>
          {policy.carrier}
        </h3>
        {policy.priority_type && (
          <Pill color={policy.priority_type === 'Inactive' ? 'destructive' : 'neutral'} size="sm">
            {policy.priority_type}
          </Pill>
        )}
        {policy.type && (
          <Pill variant="outline" color="neutral" size="sm">
            {policy.type}
          </Pill>
        )}
        {policy.is_default && <span className="text-xs text-uniform-gray-500">Default</span>}
        {isExpired && policy.plan_end_date && (
          <Pill color="destructive" size="sm">
            {`Plan ended ${formatShortDate(policy.plan_end_date)}`}
          </Pill>
        )}
        {!isExpired && endsInDays != null && endsInDays <= ENDING_SOON_DAYS && (
          <Pill color="warning" size="sm">
            {`Ends ${formatShortDate(policy.plan_end_date as string)}`}
          </Pill>
        )}
        <CanSyncWithHealthieIcon insurancePolicy={policy} />
        <div className="grow" />
        <PolicyLinks policy={policy} personId={personId} />
        <span aria-hidden="true" className="h-5 w-px bg-uniform-gray-300" />
        <Button
          color="primary"
          variant={needsAttention ? 'default' : 'outline'}
          size="sm"
          leftIcon={isLocalRunActive ? faSpinner : faShieldHalved}
          disabled={isLocalRunActive}
          onClick={() => onVerify(policy)}>
          {isLocalRunActive ? 'Albert running…' : canRunVob ? 'Verify' : 'Add verification'}
        </Button>
        <span className="relative inline-flex">
          <Button
            variant="outline"
            color="neutral"
            size="icon-sm"
            aria-label={
              policy.is_mutable === false ? 'Edit policy (locked: verified)' : 'Edit policy'
            }
            onClick={() => setIsEditing((editing) => !editing)}>
            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
          </Button>
          {policy.is_mutable === false && (
            <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border border-white bg-uniform-gray-200 text-uniform-gray-600">
              <FontAwesomeIcon icon={faLock} className="h-2 w-2" />
            </span>
          )}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger aria-label="More actions">
            <Button variant="ghost" color="neutral" size="icon-sm" aria-hidden tabIndex={-1}>
              <FontAwesomeIcon icon={faEllipsis} className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" align="end">
            {policy.insurance_card_images?.id && (
              <DropdownMenuItem
                href={`${host}/agent/audit_logs/insurancecardimage/${policy.insurance_card_images.id}`}
                target="_blank">
                Card image history
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => navigate(`/people/${personId}/insurance`)}>
              Open on Insurance page
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                navigator.clipboard.writeText(policy.id ?? '');
                addMessage('success', 'Policy ID copied.');
              }}>
              Copy policy ID
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setIsDetailsOpen(true);
              }}>
              Debug
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isEditing ? (
        <div className="p-4">
          <PolicyEditor policy={policy} onClose={() => setIsEditing(false)} />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            <div className="flex flex-col gap-2.5 border-uniform-gray-100 p-4 text-sm md:border-r">
              <div className="text-xs font-semibold uppercase tracking-wide text-uniform-gray-500">
                Policy
              </div>
              <div className="flex gap-3">
                <span className="w-24 shrink-0 text-uniform-gray-500">Plan</span>
                {policy.plan ? (
                  <span className="min-w-0 font-medium">
                    <CopyContent content={policy.plan} />
                  </span>
                ) : (
                  <span className="text-uniform-gray-400">—</span>
                )}
              </div>
              <div className="flex gap-3">
                <span className="w-24 shrink-0 text-uniform-gray-500">Subscriber ID</span>
                {policy.subscriber_id ? (
                  <span className="min-w-0 break-all font-mono text-[12.5px] font-medium">
                    <CopyContent content={policy.subscriber_id} />
                  </span>
                ) : (
                  <span className="text-uniform-gray-400">—</span>
                )}
              </div>
              {policy.policy_member_id && (
                <div className="flex gap-3">
                  <span className="w-24 shrink-0 text-uniform-gray-500">Member ID</span>
                  <span className="min-w-0 break-all font-mono text-[12.5px] font-medium">
                    <CopyContent content={policy.policy_member_id} />
                  </span>
                </div>
              )}
              <div className="flex gap-3">
                <span className="w-24 shrink-0 text-uniform-gray-500">Coverage</span>
                <span className="flex min-w-0 flex-col">
                  <span className="font-medium">
                    {policy.plan_start_date || policy.plan_end_date
                      ? `${policy.plan_start_date ? formatShortDate(policy.plan_start_date) : '—'} → ${policy.plan_end_date ? formatShortDate(policy.plan_end_date) : 'open'}`
                      : '—'}
                  </span>
                  {isExpired && policy.plan_end_date && (
                    <span className="text-red-700">
                      Ended {formatShortDate(policy.plan_end_date)}
                    </span>
                  )}
                  {!isExpired && endsInDays != null && (
                    <span
                      className={
                        endsInDays <= ENDING_SOON_DAYS ? 'text-yellow-800' : 'text-uniform-gray-500'
                      }>
                      Ends in {endsInDays} days
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-24 shrink-0 text-uniform-gray-500">Cards</span>
                {policy.insurance_card_images?.front_image_url ||
                policy.insurance_card_images?.back_image_url ? (
                  <span className="flex gap-2">
                    {policy.insurance_card_images.front_image_url && (
                      <CardImageThumbnail
                        label="Front"
                        url={policy.insurance_card_images.front_image_url}
                        isUpdatedAfterVerification={isCardUpdatedAfterVerification(
                          policy.insurance_card_images.updated_at,
                          verification?.verified_at
                        )}
                        updatedAt={policy.insurance_card_images.updated_at}
                        verifiedAt={verification?.verified_at}
                      />
                    )}
                    {policy.insurance_card_images.back_image_url && (
                      <CardImageThumbnail
                        label="Back"
                        url={policy.insurance_card_images.back_image_url}
                        isUpdatedAfterVerification={isCardUpdatedAfterVerification(
                          policy.insurance_card_images.updated_at,
                          verification?.verified_at
                        )}
                        updatedAt={policy.insurance_card_images.updated_at}
                        verifiedAt={verification?.verified_at}
                      />
                    )}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <span className="text-uniform-gray-500">No card images</span>
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="text-sm text-blue-700">
                      Upload
                    </button>
                  </span>
                )}
              </div>
              <div className="mt-auto pt-1">
                <ExpanderButton
                  label="All policy details"
                  isOpen={isDetailsOpen}
                  onToggle={() => setIsDetailsOpen((open) => !open)}
                />
              </div>
            </div>
            {verificationColumn}
          </div>

          {isPayerResponseOpen && verification && (
            <div className="border-t border-uniform-gray-100 p-4">
              <PayerResponse
                verification={verification}
                view="full"
                isLoading={isDetailLoading}
                fullEpv={fullPolicy?.last_verification?.external_policy_verification}
                timelineUrl={
                  policy.id && epv
                    ? billingTimelineUrl(policy.id, {
                        targetDate: epv.created_at,
                        event: { type: 'external_policy_verification', id: epv.id }
                      })
                    : null
                }
              />
            </div>
          )}

          {isDetailsOpen && (
            <div className="border-t border-uniform-gray-100 p-4">
              <PolicyDetailsList policy={policy} />
            </div>
          )}
        </>
      )}
    </article>
  );
};

export default React.memo(InsurancePolicyCard);
